import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import FormSection from './components/FormSection';
import { determineFrequency, handleEnableDatasetDateChange } from './utils';
import { OptionDefinition } from 'src/common/types/OptionDefinition';
import { SelectChangeEvent } from 'src/common/types/Events';
import { defaultSchedule, Schedule } from 'src/common/types';
import { FrequencyOption } from 'src/common/constants/frequency';
import { getFcFixedAllocationTaskInput } from 'src/common/utils/task';
import { Environment } from 'src/common/constants/environment';
import { TaskSchedulingServiceApi } from 'src/api/TaskSchedulingServiceApi';
import { convertTime, getTimezoneOffset } from 'src/common/utils/date';
import { useNotificationContext } from 'src/common/provider/NotificationProvider';
import { Notification } from 'src/common/constants/Notification';
import ScheduleSection from 'src/fcfixed-allocations/pages/create-fcfixed-allocation-page/components/ScheduleSection';
import { Methodology } from 'src/common/constants/Methodology';

const FCFIXED_DRIVER_PREFIX = 'FCFIXED_DRIVER';

const CreateFCFixedDriverPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedScenario, setSelectedScenario] =
    useState<OptionDefinition | null>(null);
  const [selectedSlice, setSelectedSlice] = useState<OptionDefinition | null>(
    null,
  );
  const [selectedRegion, setSelectedRegion] = useState<OptionDefinition | null>(
    null,
  );
  const [selectedTaskFrequency, setSelectedTaskFrequency] =
    useState<OptionDefinition | null>(null);
  const [schedule, setSchedule] = useState<Schedule>(defaultSchedule);
  const { addNotification } = useNotificationContext();
  const [selectedFrequency, setSelectedFrequency] =
    useState<FrequencyOption | null>(null);
  const [enableDatasetDate, setEnableDatasetDate] = useState<boolean>(false);
  const [datasetDate, setDatasetDate] = useState<string>('');

  const createTask = useMutation({
    mutationFn: TaskSchedulingServiceApi.createTask,
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const generatedName = useMemo(() => {
    if (
      selectedTaskFrequency?.value &&
      selectedScenario?.value &&
      selectedRegion?.value
    ) {
      return `${FCFIXED_DRIVER_PREFIX}_${selectedTaskFrequency.value}_${selectedRegion.value}_${selectedScenario.value}`;
    }
    if (selectedTaskFrequency?.value && selectedScenario?.value) {
      return `${FCFIXED_DRIVER_PREFIX}_${selectedTaskFrequency.value}_${selectedScenario.value}`;
    }
    return '';
  }, [selectedTaskFrequency, selectedScenario, selectedRegion]);

  // Update frequency based on generated task name
  useEffect(() => {
    if (schedule.enable && generatedName) {
      const frequency = determineFrequency(generatedName);
      setSelectedFrequency(frequency);
    } else {
      setSelectedFrequency(null);
    }
  }, [schedule.enable, generatedName]);

  const getSchedule = (schedule: Schedule) => ({
    workdayStart: Number(schedule.workdayStart?.value),
    workdayEnd: Number(schedule.workdayEnd?.value),
    scheduleTime:
      schedule.time?.value &&
      convertTime(schedule.time.value, getTimezoneOffset()),
    frequency: selectedTaskFrequency?.value,
  });

  const handleCancel = useCallback(() => {
    startTransition(() => navigate('/'));
  }, [navigate]);

  const handleScenarioChange = useCallback((event: SelectChangeEvent) => {
    const newScenario = event.detail.selectedOption as OptionDefinition;
    setSelectedScenario(newScenario);
    setSelectedRegion(null);
    setSelectedSlice(null);
  }, []);

  const handleRegionChange = useCallback((event: SelectChangeEvent) => {
    setSelectedRegion(event.detail.selectedOption as OptionDefinition);
  }, []);

  const handleEnableDatasetDateChangeCallback = useCallback(
    (checked: boolean) => {
      handleEnableDatasetDateChange(
        checked,
        setEnableDatasetDate,
        setDatasetDate,
      );
    },
    [],
  );

  const handleDatasetDateChange = useCallback((value: string) => {
    setDatasetDate(value);
  }, []);

  const handleFrequencyChange = useCallback((event: SelectChangeEvent) => {
    setSelectedTaskFrequency(event.detail.selectedOption as OptionDefinition);
  }, []);

  const handleSubmit = async () => {
    try {
      const taskParams = {
        name: generatedName,
        scenario: selectedScenario?.value,
        slices: selectedSlice?.value ? [selectedSlice.value] : undefined,
        environment: Environment.PRODUCTION,
        methodology: Methodology.FCFIXED_ALLOCATION,
        business: undefined,
        region: selectedRegion?.value,
        datasetDate: enableDatasetDate ? datasetDate : undefined,
      };

      const fcFixedDriverTaskInput = getFcFixedAllocationTaskInput(
        taskParams.scenario,
        taskParams.environment,
        taskParams.methodology,
        taskParams.business,
        taskParams.region,
        taskParams.datasetDate,
      );

      const createTaskRequest = {
        name: generatedName,
        description: 'FCFixed driver task',
        input: fcFixedDriverTaskInput,
        schedule: schedule.enable ? getSchedule(schedule) : undefined,
      };

      await createTask.mutateAsync(createTaskRequest);
      addNotification({
        type: 'success',
        content: t(Notification.driver.create.success),
      });

      startTransition(() => navigate('/fcfixeddriver/manage'));
    } catch (error: any) {
      addNotification({
        type: 'error',
        header: t(Notification.driver.create.error),
        content: error?.message,
      });
    }
  };

  return (
    <div data-testid="create-fcfixed-driver-page">
      <SpaceBetween size="l">
        <FormSection
          selectedScenario={selectedScenario}
          selectedRegion={selectedRegion}
          selectedFrequency={selectedTaskFrequency}
          generatedName={generatedName}
          enableDatasetDate={enableDatasetDate}
          datasetDate={datasetDate}
          onScenarioChange={handleScenarioChange}
          onRegionChange={handleRegionChange}
          onFrequencyChange={handleFrequencyChange}
          onEnableDatasetDateChange={handleEnableDatasetDateChangeCallback}
          onDatasetDateChange={handleDatasetDateChange}
        />
        <ScheduleSection
          schedule={schedule}
          selectedFrequency={selectedFrequency}
          selectedSlice={!!selectedTaskFrequency}
          selectedScenario={!!selectedScenario}
          onScheduleChange={setSchedule}
        />
        <Box float="right">
          <SpaceBetween direction="horizontal" size="m">
            <Button data-testid="cancel" onClick={handleCancel}>
              {t('cancel')}
            </Button>
            <Button
              data-testid="create"
              variant="primary"
              disabled={
                !selectedScenario || !selectedTaskFrequency || !selectedRegion
              }
              onClick={handleSubmit}
            >
              {t('create')}
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </div>
  );
};

export default CreateFCFixedDriverPage;
